import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import { NormalizedCacheObject } from '@apollo/client';
import { ssr } from '../../../../api/ssr.api';
import { dashify } from './utils/guid';
import settingsParams from '../../settingsParams';
import { PRODUCTION_HOSTS } from '@wix/wix-vod-constants/dist/env/hosts';
import { FetchModule, FetchModuleProps } from './worker/fetch/fetchModule';
import { createModules, ModulesProps } from './worker/modules';
import appConfig from '../../../../../.application.json';
import { widgetLoaded } from '@wix/bi-logger-video/v2';

export type ControllerProps = {
  gqlHost: string;
  instance: string;
  initialCacheData?: NormalizedCacheObject;
} & FetchModuleProps &
  ModulesProps;

const createController: CreateControllerFn = async (params) => {
  // For more info about controller structure,
  // check the docs: https://bo.wix.com/pages/yoshi/docs/editor-flow/structure-api/component/ooi-component#controller
  const {
    flowAPI: {
      bi,
      settings,
      httpClient,
      environment: { isSSR },
    },
    controllerConfig: {
      setProps,
      appParams: { instance },
      wixCodeApi: {
        window: { viewMode },
        location: { baseUrl, query },
        site: { onInstanceChanged },
      },
    },
  } = params;

  const channelId = dashify(settings.get(settingsParams.channelId)) as string;

  if (bi) {
    bi.report(
      widgetLoaded({
        channelID: channelId,
      }),
    );
  }

  const vodNoCache = query.vodNoCache === 'true' && !isSSR;

  onInstanceChanged((event) => {
    // update instance after user sign in/out.
    // clear the cache to force the client to refetch data with new instance
    const props: Partial<ControllerProps> = {
      instance: event.instance,
      initialCacheData: undefined,
    };
    setProps(props);
  }, appConfig.appDefinitionId);

  return {
    async pageReady() {
      let initialCacheData;
      const gqlHost = viewMode === 'Site' ? baseUrl : PRODUCTION_HOSTS.manage;

      if (!vodNoCache) {
        try {
          const { data } = await httpClient.request(
            ssr(gqlHost, instance, channelId),
          );

          initialCacheData = data;
        } catch (e) {
          console.log('### error', e);
        }
      }

      const fetchModule = new FetchModule(params);

      const props: ControllerProps = {
        gqlHost,
        instance,
        initialCacheData,
        ...fetchModule.getInitialProps(),
        ...(await createModules(params)),
      };

      setProps(props);
    },
  };
};

export default createController;
